import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import Seo from "../components/Seo";

export default function ResetPassword({serviceName}) {
	const canvasRef = useRef(null);
	const animationFrameId = useRef(null);
	const [form, setForm] = useState({ password: "", confirmPassword: "" });
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("info"); // info | error
	const [errors, setErrors] = useState({});
	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	// Фон с частицами (как в Auth)
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		const ctx = canvas.getContext("2d");

		function setCanvasSize() {
			canvas.width = window.innerWidth;
			canvas.height = window.innerHeight;
		}
		setCanvasSize();

		const particles = Array.from({ length: 150 }).map(() => ({
			x: Math.random() * canvas.width,
			y: Math.random() * canvas.height,
			dx: (Math.random() - 0.5) * 1.2,
			dy: (Math.random() - 0.5) * 1.2,
			size: Math.random() * 3 + 1,
			alpha: Math.random() * 0.5 + 0.1
		}));

		function animate() {
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			// Рисуем соединения между частицами
			for (let i = 0; i < particles.length; i++) {
				for (let j = i + 1; j < particles.length; j++) {
					const dx = particles[i].x - particles[j].x;
					const dy = particles[i].y - particles[j].y;
					const distance = Math.sqrt(dx * dx + dy * dy);

					if (distance < 120) {
						ctx.strokeStyle = `rgba(100, 100, 255, ${0.2 - distance / 600})`;
						ctx.lineWidth = 0.8;
						ctx.beginPath();
						ctx.moveTo(particles[i].x, particles[i].y);
						ctx.lineTo(particles[j].x, particles[j].y);
						ctx.stroke();
					}
				}
			}

			// Рисуем сами частицы
			particles.forEach((p) => {
				p.x += p.dx;
				p.y += p.dy;

				// Отскок от границ
				if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
				if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

				ctx.fillStyle = `rgba(100, 150, 255, ${p.alpha})`;
				ctx.beginPath();
				ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
				ctx.fill();
			});

			animationFrameId.current = requestAnimationFrame(animate);
		}
		animate();

		window.addEventListener("resize", setCanvasSize);
		return () => {
			window.removeEventListener("resize", setCanvasSize);
			if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
		};
	}, []);

	const handleSubmit = async (e) => {
		e.preventDefault();
		setMessage("");
		setErrors({});

		try {
			const res = await fetch("/api/reset-password", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ ...form, token }),
			});

			const data = await res.json();
			setMessage(data.message || "");
			setMessageType(data.type || "info");
			setErrors(data.errors || {});

			if (res.ok) {
				setForm({ password: "", confirmPassword: "" });

				// SPA редирект после успешного сброса пароля
				window.location.href = "/auth?reset=1";
			}
		} catch (err) {
			setMessage("Network error: " + err.message);
			setMessageType("error");
		}
	};

	const inputClass =
		"w-full px-4 py-3 rounded-xl bg-white/5 border text-white focus:outline-none focus:border-blue-400";

	const getInputClass = (field) =>
		`${inputClass} ${errors[field] ? "border-red-500 bg-red-100/10" : "border-white/20"}`;

	return (
		<>
			<Seo
				title={`${serviceName} - Сброс пароля`}
				description="Умная защита от DDoS атак, ботов и кибератак."
			/>
			<div className="relative min-h-screen bg-gray-900 text-white overflow-hidden">
				<canvas ref={canvasRef} className="absolute inset-0 z-0" />
				<div className="absolute inset-0 z-0 bg-gradient-to-br from-blue-900/40 via-purple-900/30 to-black/50"></div>

				<section className="relative z-10 flex items-center justify-center h-screen px-4">
					<motion.div
						className="bg-white/10 backdrop-blur-lg p-8 rounded-3xl shadow-xl border border-white/20 max-w-md w-full"
						initial={{ opacity: 0, y: 40 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<h2 className="text-2xl font-semibold text-center mb-6">Сброс пароля</h2>

						<AnimatePresence mode="wait">
							<motion.form
								key="reset-password"
								onSubmit={handleSubmit}
								className="space-y-5"
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								transition={{ duration: 0.3 }}
							>
								<div>
									<label className="block text-gray-300 mb-1">Новый пароль</label>
									<input
										type="password"
										className={getInputClass("password")}
										value={form.password}
										onChange={(e) => setForm({ ...form, password: e.target.value })}
										required
									/>
								</div>

								<div>
									<label className="block text-gray-300 mb-1">Подтверждение пароля</label>
									<input
										type="password"
										className={getInputClass("confirmPassword")}
										value={form.confirmPassword}
										onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
										required
									/>
								</div>

								<motion.button
									type="submit"
									className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-purple-500 hover:to-pink-500 rounded-xl font-semibold shadow-lg transition-all duration-300"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.98 }}
								>
									Сменить пароль
								</motion.button>
							</motion.form>
						</AnimatePresence>

						{message && (
							<p
								className={`mt-4 text-center text-sm ${messageType === "info" ? "text-green-400" : "text-red-500"
									}`}
							>
								{message}
							</p>
						)}
					</motion.div>
				</section>
			</div>
		</>
	);
}

